import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { formatCount } from '@/utils/number';
import type { Role } from '../types/role.types';

interface RoleColumnActions {
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
}

export function getRoleColumns(
  searchText?: string,
  actions?: RoleColumnActions,
): ColumnDef<Role, unknown>[] {
  return [
    createSerialColumn<Role>(),
    {
      accessorKey: 'name',
      header: 'Role Name',
      cell: ({ row }) => (
        <span className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Details',
      cell: ({ row }) => {
        const description = row.original.description;
        if (!description) return <EmptyValue />;
        return (
          <WrappingCellText width="wide">
            <HighlightText text={description} highlightedText={searchText} />
          </WrappingCellText>
        );
      },
    },
    {
      accessorKey: 'userTypeName',
      header: 'User Type',
      cell: ({ row }) => row.original.userTypeName ?? '—',
    },
    {
      accessorKey: 'assignedUserCount',
      header: 'Users',
      cell: ({ row }) => (
        <span className="tabular-nums" title={formatCount(row.original.assignedUserCount, 'user')}>
          {row.original.assignedUserCount}
        </span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    ...(actions
      ? [
          createActionsColumn<Role>((role) => (
            <RowEditDeleteButtons
              item={role}
              itemName={role.name}
              onEdit={actions.onEdit}
              onDelete={actions.onDelete}
            />
          )),
        ]
      : []),
  ];
}
